/**
 * Labstep
 *
 * @module grid/NewIndex/coldefs/metadata/sequence
 * @desc Column Definition for metadata sequence
 */

import type {
  EditableCallbackParams,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { ICONS } from 'labstep-web/constants/icons';
import { suppressKeyboardEventClearCells } from 'labstep-web/core/DataGrid/CellEditor/utils';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import type {
  CellEditorParams,
  CellRendererParams,
} from 'labstep-web/core/DataGrid/types';
import {
  createOrUpdateValue,
  setValue,
  stopEditingAndFocus,
} from 'labstep-web/core/DataGrid/utils';
import { SequenceEditor } from 'labstep-web/core/SequenceEditor';
import { TextPlaceholder } from 'labstep-web/core/Text/Placeholder';
import { getMetadata } from 'labstep-web/grid/NewIndex/coldefs';
import type { NewIndexColDef } from 'labstep-web/grid/NewIndex/coldefs/types';
import { GridNewIndexColumnHeader } from 'labstep-web/grid/NewIndex/components/ColumnHeader';
import { GridNewIndexService } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridColumnDefinitionService } from 'labstep-web/grid/services/grid-column-definition.service';
import type { Entity } from 'labstep-web/models/entity.model';
import { Metadata } from 'labstep-web/models/metadata';
import type { MetadataType } from 'labstep-web/models/metadata/types';
import { MetadataThread } from 'labstep-web/models/metadata-thread.model';
import { generateNewDateString } from 'labstep-web/services/date.service';
import rules from 'labstep-web/services/validation/rules';

export const getMetadataSequenceColDef = (
  parentEntityClass: string,
  type: MetadataType,
  metadataLabel: string,
  entityName?: string,
): NewIndexColDef => {
  return {
    colId: `metadata:${parentEntityClass}:${type}:${metadataLabel}`,
    sortable: true,
    headerName: metadataLabel,
    headerComponent: GridNewIndexColumnHeader,
    headerComponentParams: {
      icon: ICONS.metadata.type.sequence,
      postFilterSettings: {
        path:
          parentEntityClass !== entityName
            ? `${parentEntityClass}.metadatas`
            : 'metadatas',
      },
    },
    editable: (params: EditableCallbackParams) => {
      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return false;
      }
      return GridNewIndexService.getEditable(params);
    },
    valueGetter: (params: ValueGetterParams) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata) {
        return null;
      }

      return metadata.sequence?.sequenceData?.sequence;
    },
    valueSetter: (params: ValueSetterParams) => {
      if (!params.node) {
        return false;
      }
      return false;
    },
    cellRenderer: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }

      const entity = params.node.data;
      if (
        parentEntityClass === 'purchase_order' &&
        !entity.purchase_order
      ) {
        return null;
      }

      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );
      const metadataThreadId =
        parentEntityClass !== params.node.data.entityName
          ? params.node.data[parentEntityClass].metadata_thread.idAttr
          : params.node.data.metadata_thread.idAttr;

      if (!metadata) {
        if (
          parentEntityClass !== params.node.data.entityName &&
          !params.node.data[parentEntityClass]
        ) {
          return false;
        }
        const props = {
          parentName: MetadataThread.entityName,
          parentId: metadataThreadId,
        };
        return (
          <TextPlaceholder
            onClick={() => {
              createOrUpdateValue({
                entityName: Metadata.entityName,
                body: {
                  type,
                  label: metadataLabel,
                  metadata_thread_id: metadataThreadId,
                  sequence: { name: 'Untitled sequence' },
                },
                createProps: props,
                options:
                  GridColumnDefinitionService.getOptions(params),
              });
              return true;
            }}
          >
            Create sequence
          </TextPlaceholder>
        );
      }

      if (!metadata.sequence?.sequenceData?.sequence) {
        return (
          <DataGridPlaceholder
            params={params}
            editable={GridNewIndexService.getEditable(params)}
            children={'Edit sequence'}
            placeholder="Edit sequence"
          />
        );
      }
      return metadata.sequence?.sequenceData?.sequence;
    },
    cellEditorPopup: true,
    cellEditor: (params: CellRendererParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (!metadata || !metadata.sequence?.sequenceData) {
        return '';
      }

      return (
        <SequenceEditor
          sequenceData={metadata.sequence?.sequenceData}
          onClose={(): void => {
            stopEditingAndFocus(params);
          }}
          readOnly={false}
          onSave={(sequenceData) => {
            const { name, ...data } = sequenceData;
            const newValue = { name, data: JSON.stringify(data) };
            setValue(
              'sequence',
              newValue,
              {
                entityName: metadata.entityName,
                id: metadata.idAttr,
              },
              rules.metadata.sequence,
              GridColumnDefinitionService.getOptions(params),
            );
          }}
        />
      );
    },
    suppressKeyboardEvent: suppressKeyboardEventClearCells,
    valueClearer: (params: CellEditorParams<Entity>) => {
      if (!params.node) {
        return null;
      }
      const metadata = getMetadata(
        parentEntityClass,
        params.node.data,
        metadataLabel,
        type,
      );

      if (metadata) {
        setValue(
          'deleted_at',
          generateNewDateString(),
          {
            entityName: metadata.entityName,
            id: metadata.idAttr,
          },
          rules.metadata.date,
          GridColumnDefinitionService.getOptions(params),
        );
      }
      return null;
    },
  };
};
